import type { FieldChange } from '@common/schemas/entities/event-change.schema';
import { applyPatch } from '../text-versioning';

/**
 * Applies an array of delta operations (FieldChange) to a base object.
 * Returns a new mutated object representing the final state.
 */
export function applyDeltas<T>(base: T, changes: FieldChange[]): T {
  // Use a Record<string, unknown> type for the intermediate state to avoid repeated inline casts
  const result: Record<string, unknown> = structuredClone(base) as Record<string, unknown>;

  for (const change of changes) {
    const { field, operation = 'set', newValue, value, identifier } = change;
    const finalValue = value !== undefined ? value : newValue;

    if (field === 'primaryImageId') {
      const images = result.images;
      const candidates = [newValue, value];
      const matchingId = Array.isArray(images) ? candidates.find(candidate => (
        typeof candidate === 'string' && images.some(image => (
          typeof image === 'object' && image !== null && 'id' in image && image.id === candidate
        ))
      )) : undefined;
      if (typeof matchingId === 'string') {
        result.primaryImageId = matchingId;
      }
      continue;
    }

    switch (operation) {
      case 'set':
        result[field] = finalValue;
        break;
      
      case 'add': {
        const target = result[field];
        if (!Array.isArray(target)) {
          result[field] = [finalValue];
        } else {
          target.push(finalValue);
        }
        break;
      }
        
      case 'remove': {
        const target = result[field];
        if (Array.isArray(target) && identifier !== undefined) {
          result[field] = target.filter((item: unknown) => {
            // If it's an array of strings
            if (typeof item === 'string') return item !== identifier;
            // Safely check properties on objects without using 'any'
            if (typeof item === 'object' && item !== null) {
              if ('id' in item && item.id === identifier) return false;
              if ('characterId' in item && item.characterId === identifier) return false;
              if ('organizationId' in item && item.organizationId === identifier) return false;
            }
            return true; // Keep if we can't identify
          });
        }
        break;
      }
        
      case 'update': {
        const target = result[field];
        if (typeof target === 'string' && typeof finalValue === 'string') {
          // If the finalValue looks like a dmp patch format (e.g. starts with "@@ ")
          if (finalValue.startsWith('@@ ')) {
            try {
              const [newText, success] = applyPatch(target, finalValue);
              
              if (success) {
                result[field] = newText;
              } else {
                // Fallback if the patch application encounters conflicts
                if (change.newValue !== undefined) {
                  result[field] = change.newValue;
                } else {
                  result[field] = newText; // Best-effort patching
                }
              }
            } catch (err) {
              // Fallback if parsing fails
              if (change.newValue !== undefined) {
                result[field] = change.newValue;
              }
            }
          } else {
            result[field] = finalValue;
          }
        } else {
          result[field] = finalValue;
        }
        break;
      }
    }
  }

  // TODO: The result is cast back to T because this is a generic utility function dealing with fully dynamic field names on generic types, meaning TS cannot track the mutations statically.
  return result as unknown as T;
}
